import { useCallback, useRef, useState } from 'react';
import {
  TaskBlockTemplate,
  MessageBlock,
  ContentTemplate,
  LayoutBlock
} from '@dadohr/shared/domainTypes/experience';
import { useAIMultiBlockGenerator } from '../components/AIMultiBlockGenerator';
import { mockGenerateContentBlocks } from '../components/AIMultiBlockGenerator/mockBlockAPI';
import { TaskBuilderViewMode, MessageBuilderViewMode } from './types';

/**
 * Shared hook for AI block generation in TaskBuilder and MessageBuilder
 * Manages dialog state, insertion logic, and progressive block generation
 *
 * Note: Currently only generates ContentTemplate and LayoutBlock types.
 * SubTaskTemplate generation will be added in future iterations.
 */
export const useBlockBuilderAI = <T extends TaskBlockTemplate | MessageBlock>(
  currentBlocksLength: number,
  onAddBlocks: (blocks: T[], index: number, audiences?: string[]) => void,
  viewMode: TaskBuilderViewMode | MessageBuilderViewMode
) => {
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const insertionIndexRef = useRef(0);

  // Use ContentTemplate | LayoutBlock as the generated type since that's what mockGenerateContentBlocks returns
  const { state, error, generateAndInsert } = useAIMultiBlockGenerator<
    ContentTemplate | LayoutBlock
  >((blocks) => {
    // Use current insertion index and increment it
    const insertIndex = insertionIndexRef.current;
    insertionIndexRef.current += blocks.length;

    console.log(
      `[BlockBuilder] Inserting ${blocks.length} block(s) at index ${insertIndex}`
    );

    // Determine which audiences to add to
    // When in AUDIENCES_DETAIL mode, restrict to specific audience (empty array handled by caller)
    const restrictAudiences =
      viewMode.mode === 'AUDIENCES_DETAIL' ? [] : undefined;

    // Cast to T[] since ContentTemplate | LayoutBlock is always assignable to T
    // (T is either TaskBlockTemplate or MessageBlock, both of which include ContentTemplate | LayoutBlock)
    onAddBlocks(blocks as T[], insertIndex, restrictAudiences);
  });

  const handleAIGenerate = useCallback(
    async (prompt: string) => {
      // Reset insertion index to insert at end of current blocks
      insertionIndexRef.current = currentBlocksLength;

      // Close dialog immediately so user can see blocks being inserted
      setAiDialogOpen(false);

      // Generate and progressively insert blocks
      await generateAndInsert(prompt, mockGenerateContentBlocks);
    },
    [generateAndInsert, currentBlocksLength]
  );

  return {
    aiDialogOpen,
    setAiDialogOpen,
    aiGeneratorState: state,
    aiGeneratorError: error,
    handleAIGenerate
  };
};
